/**
 * Composable — slug-aware route builder.
 *
 * On the shared marketplace domain (booking.gopgms.com / localhost) every route
 * is prefixed with `/:accountSlug`.  On whitelabel domains the flat (non-prefixed)
 * route names are used.
 *
 * Usage:
 *   const { buildRoute, accountSlug } = useAccountRoute()
 *   router.push(buildRoute({ name: 'property-list', query: { ... } }))
 *
 * `buildRoute` transparently converts `{ name: 'property-list' }` into
 * `{ name: 'slug-property-list', params: { accountSlug: '...' } }` when needed.
 */

import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useAccountStore } from '@/stores/account'

export function useAccountRoute() {
  const accountStore = useAccountStore()
  const route = useRoute()

  /**
   * The current account slug — either from the URL param (shared domain)
   * or from the store (whitelabel).
   */
  const accountSlug = computed(() =>
    route.params?.accountSlug || accountStore.slug || null
  )

  /**
   * Returns true when the current context requires slug-prefixed routes.
   */
  const needsSlugPrefix = computed(() =>
    accountStore.isSharedDomain && !!accountSlug.value
  )

  /**
   * Convert a "flat" route descriptor to its slug-prefixed equivalent
   * when running on the shared domain.
   *
   * @param {Object} routeConfig — Vue Router location object, e.g.
   *   { name: 'property-details', params: { slug, propertyId }, query: { ... } }
   * @returns {Object} — The (possibly rewritten) route location.
   */
  function buildRoute(routeConfig) {
    if (!routeConfig) return routeConfig

    // If navigating by path (string or { path: '...' }), return as-is.
    // Slug-prefixed path navigation is handled manually where needed.
    if (typeof routeConfig === 'string' || routeConfig.path != null) {
      if (typeof routeConfig === 'string' && needsSlugPrefix.value) {
        const prefix = `/${accountSlug.value}`
        const path = routeConfig === '/' ? '' : routeConfig
        return `${prefix}${path}`
      }
      return routeConfig
    }

    // Named route — rewrite name to slug-prefixed variant
    if (needsSlugPrefix.value && routeConfig.name) {
      const slugName = routeConfig.name.startsWith('slug-')
        ? routeConfig.name
        : `slug-${routeConfig.name}`

      return {
        ...routeConfig,
        name: slugName,
        params: {
          ...routeConfig.params,
          accountSlug: accountSlug.value
        }
      }
    }

    return routeConfig
  }

  return {
    accountSlug,
    needsSlugPrefix,
    buildRoute
  }
}
